<script setup>
import { useApi } from '@/utils/api'
import { requiredValidator } from '@core/utils/validators'

definePage({
  meta: {
    layout: 'default',
    action: 'create',
    subject: 'Ticket',
  },
})

const router = useRouter()

const PRIORITY_OPTIONS = [
  { title: 'Rendah', value: 'low' },
  { title: 'Sedang', value: 'medium' },
  { title: 'Tinggi', value: 'high' },
  { title: 'Urgent', value: 'urgent' },
]

const CATEGORY_OPTIONS = [
  { title: 'Tagihan', value: 'billing' },
  { title: 'Teknis', value: 'technical' },
  { title: 'Request Fitur', value: 'feature_request' },
  { title: 'Lainnya', value: 'other' },
]

const formRef = ref()

const form = ref({
  subject: '',
  description: '',
  priority: 'medium',
  category: null,
})

const saving = ref(false)
const errors = ref({})
const generalError = ref('')

const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid)
    return

  saving.value = true
  errors.value = {}
  generalError.value = ''

  try {
    const response = await useApi('/api/v1/tickets').post({ body: form.value })
    const body = response?.data ?? response
    const ticket = body?.data ?? body

    router.push({ name: 'support-id', params: { id: ticket.id } })
  } catch (err) {
    const status = err?.status ?? err?.response?.status
    const data = err?.data ?? err?.response?._data

    if (status === 422 && data?.errors)
      errors.value = data.errors
    else
      generalError.value = data?.message ?? 'Gagal membuat tiket. Silakan coba lagi.'
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <div>
    <VBreadcrumbs
      :items="[
        { title: 'Bantuan / Support', to: { name: 'support' } },
        { title: 'Buat Tiket Baru', disabled: true },
      ]"
      class="px-0 mb-4"
    />

    <VRow justify="center">
      <VCol
        cols="12"
        md="8"
        lg="7"
      >
        <VCard>
          <VCardTitle class="pa-6 pb-0">
            Buat Tiket Baru
          </VCardTitle>
          <VCardText class="pa-6">
            <VAlert
              v-if="generalError"
              type="error"
              variant="tonal"
              class="mb-4"
              closable
              @click:close="generalError = ''"
            >
              {{ generalError }}
            </VAlert>

            <VForm
              ref="formRef"
              @submit.prevent="submit"
            >
              <VRow>
                <VCol cols="12">
                  <VTextField
                    v-model="form.subject"
                    label="Subjek *"
                    placeholder="Ringkasan singkat masalah/pertanyaan Anda"
                    :rules="[requiredValidator]"
                    :error-messages="errors.subject"
                  />
                </VCol>

                <VCol
                  cols="12"
                  sm="6"
                >
                  <VSelect
                    v-model="form.priority"
                    :items="PRIORITY_OPTIONS"
                    label="Prioritas *"
                    :rules="[requiredValidator]"
                    :error-messages="errors.priority"
                  />
                </VCol>

                <VCol
                  cols="12"
                  sm="6"
                >
                  <VSelect
                    v-model="form.category"
                    :items="CATEGORY_OPTIONS"
                    label="Kategori (opsional)"
                    clearable
                    :error-messages="errors.category"
                  />
                </VCol>

                <VCol cols="12">
                  <VTextarea
                    v-model="form.description"
                    label="Deskripsi *"
                    placeholder="Jelaskan keluhan/pertanyaan Anda selengkap mungkin"
                    rows="6"
                    :rules="[requiredValidator]"
                    :error-messages="errors.description"
                  />
                </VCol>
              </VRow>
            </VForm>
          </VCardText>
          <VDivider />
          <VCardActions class="pa-6">
            <VSpacer />
            <VBtn
              variant="tonal"
              color="secondary"
              :disabled="saving"
              :to="{ name: 'support' }"
            >
              Batal
            </VBtn>
            <VBtn
              color="primary"
              :loading="saving"
              @click="submit"
            >
              Kirim Tiket
            </VBtn>
          </VCardActions>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>
